/**
 * Agent Group Section
 * Renders a collapsible group of agents with drag-and-drop support
 */

import React, { useState } from 'react';
import type { AgentGroup } from '../../services/agentGroupService';
import type { Agent } from '../../types/agent';
import { AgentStatusCard } from './AgentStatusCard';
import { ChevronDown, ChevronRight, Edit2, Trash2 } from 'lucide-react';

interface AgentGroupSectionProps {
  group: AgentGroup;
  agents: Agent[];
  onToggleCollapse: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDrop: (agentId: string) => void;
  onDragStart: (agentId: string) => void;
}

export const AgentGroupSection: React.FC<AgentGroupSectionProps> = ({
  group,
  agents,
  onToggleCollapse,
  onEdit,
  onDelete,
  onDrop,
  onDragStart,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const agentId = e.dataTransfer.getData('text/plain');
    if (agentId) {
      onDrop(agentId);
    }
  };

  return (
    <div className="mb-6">
      {/* Group Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={group.isCollapsed ? 'Expand group' : 'Collapse group'}
          >
            {group.isCollapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>
          <div className={`w-3 h-3 rounded-full ${group.color}`} />
          <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
          <span className="text-sm text-gray-500">({agents.length})</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Edit group"
          >
            <Edit2 className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-50 rounded transition-colors"
            title="Delete group"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>

      {group.description && !group.isCollapsed && (
        <p className="text-sm text-gray-600 mb-3 ml-11">{group.description}</p>
      )}

      {/* Agent Grid */}
      {!group.isCollapsed && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4 rounded-lg transition-colors ${
            isDragOver ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : 'bg-gray-50'
          }`}
        >
          {agents.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No agents in this group. Drag agents here to add them.
            </div>
          ) : (
            agents.map((agent) => (
              <div
                key={agent.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', agent.id);
                  onDragStart(agent.id);
                }}
                onDragEnd={() => setIsDragOver(false)}
                className="cursor-move"
              >
                <AgentStatusCard agent={agent} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
